import React, { useState } from 'react';
import { Button, Empty, Spin } from 'antd';
import {
  ArrowRightOutlined,
  CalendarOutlined,
  CheckOutlined,
  ClockCircleOutlined,
  MessageOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import classNames from 'classnames';
import ReactMarkdown from 'react-markdown';
import dayjs from 'dayjs';
import { ReceptionistCallDisplay, formatReceptionistCallDuration } from '../types/ReceptionistCallDisplay';
import ConciergeBellIcon from './ConciergeBellIcon';
import ReceptionistCallTranscriptDrawer from './ReceptionistCallTranscriptDrawer';
import useReceptionistCallRecordingController from '../controllers/useReceptionistCallRecordingController';

interface ReceptionistCallDetailProps {
  call: ReceptionistCallDisplay | null;
}

const ReceptionistCallDetail: React.FC<ReceptionistCallDetailProps> = ({ call }) => {
  const [isTranscriptOpen, setIsTranscriptOpen] = useState(false);
  const { state: recordingState } = useReceptionistCallRecordingController(call?.id ?? '', !!call?.hasRecording);

  if (!call) {
    return (
      <div className="receptionist-calls__detail receptionist-calls__detail--empty">
        <Empty description="Sélectionnez un appel pour voir son résumé" />
      </div>
    );
  }

  return (
    <div className="receptionist-calls__detail">
      <div className="receptionist-calls__detail-header">
        <span className="receptionist-calls__detail-owner">
          <ConciergeBellIcon />
          Réceptionniste de <strong>{call.userName || 'Non assigné'}</strong>
        </span>
        <span className="receptionist-calls__tag receptionist-calls__tag--success">Appel terminé</span>
      </div>

      <div className="receptionist-calls__detail-body">
        <div className="receptionist-calls__caller">
          <div className="receptionist-calls__avatar">{call.initials}</div>
          <div>
            <div className="receptionist-calls__caller-name">{call.callerName}</div>
            {call.callerPhone && call.callerPhone !== call.callerName && (
              <div className="receptionist-calls__caller-phone">{call.callerPhone}</div>
            )}
            {call.isNewContact && (
              <span className="receptionist-calls__tag receptionist-calls__tag--purple">
                Nouveau contact créé par {call.assistantName}
              </span>
            )}
          </div>
        </div>

        <div className="receptionist-calls__meta">
          <span>
            <CalendarOutlined /> {dayjs(call.created).format('D MMMM YYYY · HH:mm')}
          </span>
          <span>
            <ClockCircleOutlined /> {formatReceptionistCallDuration(call.durationMs)}
          </span>
          {call.motifLabel && <span className="receptionist-calls__tag receptionist-calls__tag--neutral">{call.motifLabel}</span>}
        </div>

        {call.isUrgent && (
          <div className="receptionist-calls__urgency">
            <WarningOutlined />
            <span>
              <strong>Urgent</strong>
              {call.urgencyReason && ` · ${call.urgencyReason}`}
            </span>
          </div>
        )}

        {call.hasRecording && (
          <div>
            <div className="receptionist-calls__section-label">Enregistrement de l'appel</div>
            {recordingState.isLoading ? (
              <Spin size="small" />
            ) : recordingState.url ? (
              // Un enregistrement d'appel n'a pas de piste de sous-titres à fournir.
              // eslint-disable-next-line jsx-a11y/media-has-caption
              <audio key={recordingState.url} controls preload="metadata" src={recordingState.url} style={{ width: '100%' }} />
            ) : (
              <p className="receptionist-calls__summary-empty">Enregistrement indisponible pour cet appel.</p>
            )}
          </div>
        )}

        <div className="receptionist-calls__summary">
          <div className="receptionist-calls__section-label">Résumé de l'appel</div>
          {call.hasContent ? (
            <>
              {call.title && <h2 className="receptionist-calls__summary-title">{call.title}</h2>}
              <div className="receptionist-calls__summary-markdown">
                <ReactMarkdown>{call.summaryMarkdown}</ReactMarkdown>
              </div>
            </>
          ) : (
            <p className="receptionist-calls__summary-empty">Aucun résumé disponible pour cet appel.</p>
          )}
        </div>

        {call.tasks.length > 0 && (
          <div className="receptionist-calls__tasks">
            <div className="receptionist-calls__section-label">Tâches rattachées · {call.tasks.length}</div>
            {call.tasks.map((task) => (
              <div
                key={task.id}
                className={classNames('receptionist-calls__task', { 'receptionist-calls__task--done': task.status !== 'todo' })}
              >
                {task.status === 'todo' ? <ArrowRightOutlined /> : <CheckOutlined />}
                <div>
                  <div className="receptionist-calls__task-title">{task.title}</div>
                  {task.description && <div className="receptionist-calls__task-description">{task.description}</div>}
                  {task.timestamp > 0 && (
                    <div className="receptionist-calls__task-description">
                      Échéance : {dayjs(task.timestamp).format('D MMMM YYYY · HH:mm')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {call.hasTranscript && (
          <div>
            <Button icon={<MessageOutlined />} onClick={() => setIsTranscriptOpen(true)}>
              Voir la transcription
            </Button>
          </div>
        )}
      </div>

      <ReceptionistCallTranscriptDrawer call={call} open={isTranscriptOpen} onClose={() => setIsTranscriptOpen(false)} />
    </div>
  );
};

export default ReceptionistCallDetail;
